import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Paper,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
  styled
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BuildIcon from '@mui/icons-material/Build';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CategoryIcon from '@mui/icons-material/Category';
import RecommendIcon from '@mui/icons-material/Recommend';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import StarIcon from '@mui/icons-material/Star';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SmartToyIcon from '@mui/icons-material/SmartToy';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  backgroundColor: '#fff',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
  }
}));

const UploadBox = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 300,
  border: '2px dashed #FFE302',
  borderRadius: 16,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  backgroundColor: 'rgba(255, 227, 2, 0.05)',
  '&:hover': {
    backgroundColor: 'rgba(255, 227, 2, 0.1)',
    transform: 'translateY(-5px)'
  }
}));

const AnalyzeButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FFE302',
  color: '#000',
  fontWeight: 700,
  padding: '12px 24px',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(255, 227, 2, 0.3)',
  '&:hover': {
    backgroundColor: '#f5d800',
    boxShadow: '0 6px 16px rgba(255, 227, 2, 0.4)',
    transform: 'translateY(-2px)'
  },
  '&.Mui-disabled': {
    backgroundColor: '#f5f5f5',
    color: '#999'
  }
}));

const CategoryCard = styled(Box)(({ theme, selected }) => ({
  backgroundColor: '#fff',
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: selected 
    ? '0 8px 24px rgba(255, 227, 2, 0.3)' 
    : '0 4px 12px rgba(0,0,0,0.08)',
  transition: 'all 0.3s ease',
  border: selected ? '2px solid #FFE302' : '1px solid #eee',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
    borderColor: '#FFE302'
  }
}));

const Badge = styled(Box)(({ theme, variant }) => {
  const colors = {
    trending: { bg: 'rgba(255, 227, 2, 0.15)', color: '#1a1a1a' },
    new: { bg: 'rgba(25, 118, 210, 0.15)', color: '#1976d2' },
    hot: { bg: 'rgba(211, 47, 47, 0.15)', color: '#d32f2f' },
  };
  
  return {
    backgroundColor: colors[variant]?.bg || colors.trending.bg,
    color: colors[variant]?.color || colors.trending.color,
    padding: '6px 12px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  };
});

const ExploreButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#1a1a1a',
  color: '#FFE302',
  border: 'none',
  padding: '8px 16px',
  borderRadius: 20,
  fontSize: 14,
  fontWeight: 600,
  '&:hover': {
    backgroundColor: '#000',
  }
}));

const AIButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#1a1a1a',
  color: '#FFE302',
  border: 'none',
  padding: '12px 24px',
  borderRadius: 8,
  fontSize: 16,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  '&:hover': {
    backgroundColor: '#000',
  }
}));

const ProductCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.12)',
  }
}));

const ReliabilityBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  backgroundColor: 'rgba(26, 26, 26, 0.9)',
  color: '#FFE302',
  padding: '6px 12px',
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 600,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: 28,
  fontWeight: 800,
  marginBottom: 20,
  color: '#1a1a1a',
  position: 'relative',
  display: 'inline-block',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: 80,
    height: 4,
    backgroundColor: '#FFE302',
    borderRadius: 2
  }
}));

const AIDetect = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAnalyze = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setAnalysis({
        issues: [
          {
            severity: 'high',
            title: 'Screen Damage',
            description: 'Multiple cracks detected on the screen surface',
            solution: 'Replace screen assembly',
            products: [
              { name: 'iPhone 12 Screen Replacement Kit', price: '$129.99' },
              { name: 'Professional Screen Protector', price: '$19.99' }
            ]
          },
          {
            severity: 'medium',
            title: 'Battery Health',
            description: 'Battery capacity at 72% - Consider replacement',
            solution: 'Replace battery',
            products: [
              { name: 'Genuine Battery Replacement', price: '$69.99' },
              { name: 'Battery Replacement Tool Kit', price: '$24.99' }
            ]
          }
        ]
      });
      setLoading(false);
    }, 2000);
  };

  const categories = [
    { 
      id: 1, 
      name: 'Processors', 
      icon: <CategoryIcon>⬛</CategoryIcon>, 
      count: 124,
      description: 'CPU and processing units for computing power',
      brands: ['Intel', 'AMD', 'Apple'],
      trending: true
    },
    { 
      id: 2, 
      name: 'Graphics Cards', 
      icon: <CategoryIcon>📺</CategoryIcon>, 
      count: 89,
      description: 'GPUs for gaming and professional graphics',
      brands: ['NVIDIA', 'AMD', 'Intel'],
      trending: true
    },
    { 
      id: 3, 
      name: 'Motherboards', 
      icon: <CategoryIcon>🔌</CategoryIcon>, 
      count: 67,
      description: 'System boards and chipsets',
      brands: ['ASUS', 'MSI', 'Gigabyte'],
      hot: true
    },
    { 
      id: 4, 
      name: 'Memory (RAM)', 
      icon: <CategoryIcon>💾</CategoryIcon>, 
      count: 93,
      description: 'High-speed system memory modules',
      brands: ['Corsair', 'G.Skill', 'Kingston'],
      new: true
    },
    { 
      id: 5, 
      name: 'Storage', 
      icon: <CategoryIcon>⏱️</CategoryIcon>, 
      count: 156,
      description: 'SSDs, HDDs, and storage solutions',
      brands: ['Samsung', 'Western Digital', 'Seagate'],
      featured: true
    },
  ];

  const suggestedProducts = [
    { id: 1, name: 'Intel Core i7', category: 'Processors', reliability: '98%', image: '/cpu.png' },
    { id: 2, name: 'NVIDIA RTX 4080', category: 'Graphics Cards', reliability: '95%', image: '/gpu.png' },
    { id: 3, name: 'Samsung 970 EVO', category: 'Storage', reliability: '97%', image: '/ssd.png' },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Hero Section */}
      <Box sx={{ 
        textAlign: 'center', 
        mb: 6,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -30,
          right: -100,
          width: 200,
          height: 200,
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 227, 2, 0.1)',
          zIndex: -1
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -50,
          left: -100,
          width: 150,
          height: 150,
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 227, 2, 0.15)',
          zIndex: -1
        }
      }}>
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            fontWeight: 900, 
            mb: 2,
            color: '#1a1a1a',
            textTransform: 'uppercase'
          }}
        >
        AI Device Diagnostics
      </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#666', 
            maxWidth: 700, 
            mx: 'auto',
            mb: 3
          }}
        >
          Upload an image of your device and our AI will analyze potential issues and recommend solutions
        </Typography>
      </Box>
      
      <Grid container spacing={4} sx={{ mb: 8 }}>
        <Grid item xs={12} md={6}>
          <StyledPaper>
            <Typography 
              variant="h5" 
            sx={{
                mb: 3, 
                fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
                gap: 1
            }}
          >
              <AnalyticsIcon sx={{ color: '#FFE302' }} /> Device Analysis
            </Typography>
            
            {!preview ? (
              <UploadBox
                onClick={() => document.getElementById('file-input').click()}
              >
                <CloudUploadIcon sx={{ fontSize: 60, color: '#1a1a1a' }} />
                <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
                  Upload Device Image
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Click or drag and drop
                </Typography>
                <input
                  type="file"
                  id="file-input"
                  hidden
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </UploadBox>
            ) : (
              <Box sx={{ width: '100%', position: 'relative' }}>
                <img
                  src={preview}
                  alt="Preview"
                  style={{
                    width: '100%',
                    maxHeight: 300,
                    objectFit: 'contain',
                    borderRadius: 8
                  }}
                />
                <Button
                  variant="outlined"
                  sx={{ 
                    mt: 2,
                    borderColor: '#1a1a1a',
                    color: '#1a1a1a',
                    '&:hover': {
                      borderColor: '#000',
                      backgroundColor: 'rgba(0,0,0,0.05)'
                    }
                  }}
                  onClick={() => {
                    setPreview(null);
                    setSelectedFile(null);
                    setAnalysis(null);
                  }}
                >
                  Remove Image
                </Button>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <AnalyzeButton
              disabled={!selectedFile || loading}
              onClick={handleAnalyze}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AnalyticsIcon />}
              >
                {loading ? 'Analyzing...' : 'Analyze Device'}
              </AnalyzeButton>
            </Box>
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledPaper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 3, 
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <CheckCircleOutlineIcon sx={{ color: '#FFE302' }} /> Analysis Results
            </Typography>
            
            {analysis ? (
            <Box>
              {analysis.issues.map((issue, index) => (
                  <Card 
                    key={index} 
                    sx={{ 
                      mb: 2, 
                      borderRadius: 3,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      border: `1px solid ${issue.severity === 'high' ? '#ffcdd2' : '#fff9c4'}`
                    }}
                  >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {issue.severity === 'high' ? (
                          <ErrorOutlineIcon sx={{ color: '#d32f2f' }} />
                      ) : (
                          <BuildIcon sx={{ color: '#ffa000' }} />
                      )}
                        <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
                        {issue.title}
                      </Typography>
                      <Chip
                        label={issue.severity.toUpperCase()}
                        color={issue.severity === 'high' ? 'error' : 'warning'}
                        size="small"
                          sx={{ 
                            ml: 'auto',
                            fontWeight: 600,
                            borderRadius: '12px'
                          }}
                      />
                    </Box>
                    <Typography color="textSecondary" paragraph>
                      {issue.description}
                    </Typography>
                      <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
                      Recommended Solution:
                    </Typography>
                      <Typography paragraph sx={{ backgroundColor: 'rgba(255, 227, 2, 0.1)', p: 1, borderRadius: 1 }}>
                      {issue.solution}
                    </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Recommended Products:
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {issue.products.map((product, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                              mb: 0.5,
                              p: 1,
                              borderRadius: 1,
                              '&:hover': {
                                backgroundColor: '#f5f5f5'
                              }
                          }}
                        >
                          <Typography>{product.name}</Typography>
                            <Typography fontWeight="bold" sx={{ color: '#1a1a1a' }}>{product.price}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                flexGrow: 1,
                p: 3,
                backgroundColor: 'rgba(0,0,0,0.02)',
                borderRadius: 3
              }}>
                <AnalyticsIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                <Typography variant="h6" color="textSecondary" align="center">
                  Upload and analyze a device image to see diagnostic results here
                </Typography>
            </Box>
          )}
          </StyledPaper>
        </Grid>
      </Grid>

      {/* Categories Section */}
      <Box sx={{ mb: 8 }}>
        <Box sx={{ 
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
          mb: 4,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0
        }}>
          <SectionTitle variant="h4">
            Hardware Categories
          </SectionTitle>
          
          <Box sx={{ 
    display: 'flex',
            gap: 1,
            alignSelf: isMobile ? 'flex-start' : 'auto'
          }}>
            <Button 
              variant="outlined" 
              sx={{ 
                borderColor: '#1a1a1a',
                color: '#1a1a1a',
                borderRadius: 4,
                '&:hover': {
                  borderColor: '#000',
                  backgroundColor: 'rgba(0,0,0,0.05)'
                }
              }}
            >
              Popular
            </Button>
            <Button 
              variant="outlined" 
              sx={{ 
                borderColor: '#1a1a1a',
                color: '#1a1a1a',
                borderRadius: 4,
    '&:hover': {
                  borderColor: '#000',
                  backgroundColor: 'rgba(0,0,0,0.05)'
                }
              }}
            >
              Latest
            </Button>
            <Button 
              variant="contained" 
              sx={{ 
                backgroundColor: '#1a1a1a',
                color: '#FFE302',
                borderRadius: 4,
    '&:hover': {
                  backgroundColor: '#000',
                }
              }}
            >
              Trending
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {categories.map(category => (
            <Grid item xs={12} sm={6} md={4} key={category.id}>
              <CategoryCard 
                selected={selectedCategory === category.id}
                onClick={() => setSelectedCategory(category.id)}
              >
                <Box sx={{ 
                  p: 3, 
    borderBottom: '1px solid #eee',
    display: 'flex',
    alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  {category.icon}
                  
                  {category.trending && (
                    <Badge variant="trending">
                      <LocalFireDepartmentIcon fontSize="small" /> Trending
                    </Badge>
                  )}
                  {category.new && (
                    <Badge variant="new">
                      <NewReleasesIcon fontSize="small" /> New
                    </Badge>
                  )}
                  {category.hot && (
                    <Badge variant="hot">
                      <StarIcon fontSize="small" /> Hot
                    </Badge>
                  )}
                </Box>
                
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {category.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {category.description}
                  </Typography>
                  
                  <Box sx={{ 
    display: 'flex',
    flexWrap: 'wrap',
                    gap: 1,
                    mb: 2
                  }}>
                    {category.brands.map((brand, index) => (
                      <Chip 
                        key={index} 
                        label={brand} 
                        size="small"
                        sx={{ 
                          backgroundColor: 'rgba(0,0,0,0.05)',
    color: '#666',
                          fontWeight: 500
                        }}
                      />
                    ))}
                  </Box>

                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pt: 2,
                    borderTop: '1px solid #eee'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#666' }}>
                      {category.count} items
                    </Typography>
                    <ExploreButton 
                      endIcon={<ArrowForwardIcon />}
                      size="small"
                    >
                      Explore
                    </ExploreButton>
                  </Box>
                </Box>
              </CategoryCard>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* AI Suggestions Section */}
      <StyledPaper sx={{ 
        p: 4, 
        backgroundColor: '#1a1a1a',
        mb: 4
      }}>
        <Box sx={{ 
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
          mb: 4,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0
        }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800,
              color: '#fff',
              position: 'relative',
              display: 'inline-block',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: 80,
                height: 4,
                backgroundColor: '#FFE302',
                borderRadius: 2
              }
            }}
          >
            AI-Powered Suggestions
          </Typography>
          
          <AIButton 
            startIcon={<SmartToyIcon />}
            sx={{ 
              backgroundColor: '#FFE302',
              color: '#1a1a1a',
    '&:hover': {
                backgroundColor: '#f5d800',
              }
            }}
          >
            Get Smart Recommendations
          </AIButton>
        </Box>
        
        <Grid container spacing={3}>
          {suggestedProducts.map(product => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <ProductCard>
                <Box sx={{ 
                  height: 160, 
                  backgroundColor: '#2a2a2a',
                  position: 'relative',
    display: 'flex',
    alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FFE302',
                  fontSize: 40
                }}>
                  {product.category === 'Processors' && '⬛'}
                  {product.category === 'Graphics Cards' && '📺'}
                  {product.category === 'Storage' && '⏱️'}
                  
                  <ReliabilityBadge>
                    AI Match: {product.reliability}
                  </ReliabilityBadge>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {product.name}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
    color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <CategoryIcon fontSize="small" /> {product.category}
                  </Typography>
                </Box>
              </ProductCard>
            </Grid>
          ))}
        </Grid>
      </StyledPaper>
    </Container>
  );
};

export default AIDetect; 